<template>
    <div class="flex flex-col gap-2 w-full">
        <!-- The #empty slot only renders while the list is empty, so a region inside it would be
             created together with "Searching..."; this one stays mounted and only its text changes. -->
        <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ isFetching ? t("chat.connectionDisambiguation.searching") : "" }}</span>
        <BaseCombobox
            v-model="selectedItem"
            v-model:input="searchText"
            :input-id="inputId"
            :options="connectionItems"
            :placeholder="placeholder ?? t('chat.connectionDisambiguation.placeholder')"
            :auto-open="autoOpen"
            :auto-focus="autoFocus"
            :actions="actions"
            @action="handleCreate">
            <template #empty>
                <div v-if="isFetching" class="flex gap-2 justify-center items-center p-2">
                    <LoadingSpinner class="w-4 h-4" aria-hidden="true" />
                    <p>{{ t("chat.connectionDisambiguation.searching") }}</p>
                </div>
            </template>
            <template #action="{ action }">
                <div class="flex items-center gap-3 px-3 py-2">
                    <BaseAvatar size="sm" aria-hidden="true">
                        <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
                    </BaseAvatar>
                    <span>{{ action.label }}</span>
                </div>
            </template>
            <template #item="{ option }">
                <div class="flex items-center gap-3 px-3 py-2">
                    <PeopleAvatar size="sm" :name="option?.label" />
                    <span class="flex flex-col min-w-0">
                        <span class="body-small-semibold truncate">{{ option?.label }}</span>
                        <span v-if="option?.title" class="body-small opacity-75 truncate">{{ option.title }}</span>
                    </span>
                </div>
            </template>
        </BaseCombobox>
        <div v-if="showCancel" class="flex justify-end">
            <BaseButton theme="tertiary" size="sm" @click="emit('cancel')">{{ t("common.cancel") }}</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import { i18n } from "~vue/i18n";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { Connection } from "~vue/types/connection";
import { computed, ref, watch } from "vue";

type ConnectionItem = { value: string; label: string; title?: string };

type ChatConnectionDisambiguationProps = {
    initialSearchTerm?: string;
    showCancel?: boolean;
    inputId?: string;
    placeholder?: string;
    autoOpen?: boolean;
    autoFocus?: boolean;
    excludeConnectionId?: string;
    allowCreate?: boolean;
};

const props = withDefaults(defineProps<ChatConnectionDisambiguationProps>(), {
    initialSearchTerm: "",
    showCancel: false,
    autoOpen: true,
    autoFocus: false,
    excludeConnectionId: "",
    allowCreate: false,
});

// i18n.global rather than useI18n(): ChatMyPeopleAccordion renders this and can be mounted without an app context.
const { t } = i18n.global;

const model = defineModel<Connection | null>({ default: null });

const emit = defineEmits<{
    select: [conn: Connection];
    create: [name: string];
    cancel: [];
}>();

const searchText = ref(props.initialSearchTerm);
const debouncedQuery = refDebounced(searchText, 300);
const emittedQuery = ref(props.initialSearchTerm);
const searchUrl = computed(() => `/api/connections/?search=${encodeURIComponent(emittedQuery.value)}`);

watch(debouncedQuery, (value) => {
    emittedQuery.value = value;
});

// Search is rate-limited to one request per second per user; a residual 429
// (e.g. the same user in another tab) must not clobber the results already
// shown, so the error body never replaces `data`.
const { data, isFetching } = useFetch(searchUrl, { refetch: true, updateDataOnError: false }).json<Connection[]>();
// Belt and braces with updateDataOnError: keep the last good array so stale
// results show instead of a broken list.
const lastGoodConnections = ref<Connection[]>([]);
watch(
    data,
    (value) => {
        if (Array.isArray(value)) {
            lastGoodConnections.value = value;
        }
    },
    { immediate: true },
);
const connections = computed(() => lastGoodConnections.value.filter((connection) => !props.excludeConnectionId || connection.id !== props.excludeConnectionId).slice(0, 10));

const connectionItems = computed<ConnectionItem[]>(() =>
    connections.value.map((connection) => ({
        value: connection.id,
        label: connection.persona.name ?? t("chat.connectionDisambiguation.noName"),
        title: connection.job_title,
    })),
);

const selectedItem = ref<ConnectionItem | null>(null);

const actions = computed<ComboboxAction[]>(() => {
    const name = searchText.value.trim();
    if (!props.allowCreate || !name || isFetching.value) {
        return [];
    }
    return [{ id: "create", label: t("chat.connectionDisambiguation.addNewPerson", { name }) }];
});

const handleCreate = (actionId: string) => {
    if (actionId !== "create") {
        return;
    }
    const name = searchText.value.trim();
    if (name) {
        emit("create", name);
    }
};

watch(selectedItem, (item) => {
    if (!item) {
        model.value = null;
        return;
    }
    const conn = connections.value.find((c) => c.id === item.value);
    if (conn) {
        model.value = conn;
        emit("select", conn);
    }
});
</script>
